/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone, IFocusZoneProps } from '@fluentui/react/lib/FocusZone';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/react/lib/Styling';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import utterance1 from 'src/appShell/homePage/jsonResponses/utterance1.json';
import utterance2 from 'src/appShell/homePage/jsonResponses/utterance2.json';
import utterance3 from 'src/appShell/homePage/jsonResponses/utterance3.json';
import utterance4 from 'src/appShell/homePage/jsonResponses/utterance4.json';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist, joinClassNames } from 'src/core/themes/luisStylist';

const messages = defineMessages({
    title: {
        id: 'portal.homePageDemo.title',
        defaultMessage: 'See Language Understanding in action'
    },
    utterancesTitle: {
        id: 'portal.homePageDemo.utterancesTitle',
        defaultMessage: 'What the user says (utterances)'
    },
    luisResponseTitle: {
        id: 'portal.homePageDemo.luisResponseTitle',
        defaultMessage: 'What LUIS returns'
    },
    utterance1: {
        id: 'portal.homePageDemo.utterance1',
        defaultMessage: 'Book me a flight to Cairo'
    },
    utterance2: {
        id: 'portal.homePageDemo.utterance2',
        defaultMessage: 'Order me 2 pizzas'
    },
    utterance3: {
        id: 'portal.homePageDemo.utterance3',
        defaultMessage: 'Remind me to call my dad tomorrow'
    },
    utterance4: {
        id: 'portal.homePageDemo.utterance4',
        defaultMessage: 'Where is the nearest club?'
    }
});

const { styleDiv, styleComponent, getClassName } = getStylist('HomePageDemo');

const DemoContainer = styleDiv<{ isLargeScreen: boolean }>(
    'HomePageDemoContainer',
    theme => ({
        backgroundColor: theme.containerBackgroundColor
    }),
    ({ customProps }, theme) => ({ padding: customProps.isLargeScreen ? theme.containerPaddingLarge : theme.containerPaddingSmall })
);

const DemoTitle = styleComponent<ITextProps>(Text)('HomePageDemoTitle', theme => ({ fontSize: theme.titleFontSize, textAlign: 'center' }));
const DemoContentPositioner = styleComponent<IStackProps>(Stack)('HomePageDemoContentPositioner', { width: '100%' });
const halfStackClassName = getClassName('HalfStack', { flex: 1 });
const UtteranceBulletContainer = styleComponent<IFocusZoneProps>(FocusZone)('UtteranceBulletContainer', { width: '100%' });

const UtteranceBullet = styleComponent<IStackProps>(Stack)('UtteranceBullet', theme => ({
    backgroundColor: theme.utteranceBulletBackgroundColor,
    padding: theme.utteranceBulletSpacingPadding,
    marginBottom: theme.utteranceBulletSpacing,
    cursor: 'pointer',
    textAlign: 'center',
    transition: theme.utteranceBulletTransition,
    borderRadius: theme.utteranceBulletBorderRadius,
    $nest: {
        '&:hover': {
            backgroundColor: theme.utteranceBulletBackgroundColorHover
        },
        '&:active': {
            backgroundColor: theme.utteranceBulletBackgroundColorActive
        },
        '&.selected': {
            backgroundColor: theme.utteranceBulletBackgroundColorActive
        },
        '&:last-child': { marginBottom: 0 }
    }
}));

const ResponseArea = styleComponent<IStackProps>(Stack)('HomepageResponseArea', theme => ({
    backgroundColor: theme.responseContainerBackgroundColor,
    width: '100%',
    overflow: 'auto',
    paddingLeft: theme.responseContainerPadding,
    paddingRight: theme.responseContainerPadding,
    maxHeight: theme.responseContainerMaxHeight
}));

const utteranceBulletFocusClassName = mergeStyles(getFocusStyle(getTheme()));

/**
 * Renders the utterance examples in the home page.
 */
export const HomePageDemo = () => {
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();
    const [selectedBulletIndex, setSelectedBulletIndex] = React.useState(0);

    const bulletTexts = [messages.utterance1, messages.utterance2, messages.utterance3, messages.utterance4];
    const bulletResponses = [utterance1, utterance2, utterance3, utterance4];

    return (
        <DemoContainer role="region" customProps={{ isLargeScreen: isXLargeOrGreater }}>
            <Stack horizontalAlign="center" tokens={{ childrenGap: 50 }}>
                <DemoTitle variant="xxLarge" as="h3" block>
                    {localize(messages.title)}
                </DemoTitle>

                <DemoContentPositioner
                    data-automation-id="utteranceBulletsContainer"
                    horizontal={isXLargeOrGreater}
                    tokens={{ childrenGap: 50 }}>
                    <Stack.Item className={halfStackClassName}>
                        <Stack horizontalAlign="center" tokens={{ childrenGap: 30 }}>
                            <LuisText variant="xLarge" as="h4" fontWeight={400} block>
                                {localize(messages.utterancesTitle)}
                            </LuisText>

                            <UtteranceBulletContainer role="tablist">
                                {bulletTexts.map((b, index) => (
                                    <UtteranceBullet
                                        key={`utteranceBullet-${index}`}
                                        role="tab"
                                        id={`utteranceBullet-${index}`}
                                        aria-controls={`utteranceBulletResponse-${selectedBulletIndex}`}
                                        aria-selected={index === selectedBulletIndex}
                                        data-is-focusable="true"
                                        className={joinClassNames(
                                            utteranceBulletFocusClassName,
                                            index === selectedBulletIndex ? 'selected' : ''
                                        )}
                                        onClick={() => setSelectedBulletIndex(index)}>
                                        <LuisText fontWeight={600}>{localize(b)}</LuisText>
                                    </UtteranceBullet>
                                ))}
                            </UtteranceBulletContainer>
                        </Stack>
                    </Stack.Item>

                    <Stack.Item className={halfStackClassName}>
                        <Stack horizontalAlign="center" verticalFill tokens={{ childrenGap: 30 }}>
                            <LuisText variant="xLarge" as="h4" fontWeight={400} block>
                                {localize(messages.luisResponseTitle)}
                            </LuisText>

                            <ResponseArea
                                tabIndex={0}
                                verticalFill
                                id={`utteranceBulletResponse-${selectedBulletIndex}`}
                                aria-labelledby={`utteranceBullet-${selectedBulletIndex}`}>
                                <pre>{JSON.stringify(bulletResponses[selectedBulletIndex], null, 4)}</pre>
                            </ResponseArea>
                        </Stack>
                    </Stack.Item>
                </DemoContentPositioner>
            </Stack>
        </DemoContainer>
    );
};
